import styled, { css, createGlobalStyle } from 'styled-components'
import { cardisTheme } from '../../theme/cardisTheme'

// Global print styles to remove page margins for full-bleed cover
// IMPORTANT: @page rules must be at top level, NOT inside @media print
export const CardisCoverPrintStyles = createGlobalStyle`
  @page :first {
    margin: 0 !important;
    size: A4 !important;
  }
`

const printStyles = css`
  @media print {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CoverContainer = styled.div`
  width: 100%;
  height: 311mm;
  margin: 0;
  position: relative;
  overflow: hidden;
  font-family: ${cardisTheme.fonts.body};
  box-sizing: border-box;
  background-color: ${cardisTheme.colors.backgroundGray};
  margin-left: -3.3rem;
  margin-right: -3.3rem;
  width: calc(100% + 6.6rem);

  @media print {
    display: block !important;
    position: relative !important;
    overflow: hidden !important;
    page-break-after: always !important;
    page-break-inside: avoid !important;
    background-color: ${cardisTheme.colors.backgroundGray} !important;
    ${printStyles}
  }
`

export const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    ${printStyles}
  }
`

export const OverlayImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    ${printStyles}
  }
`

export const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 48px;
  margin-top: 40px;
  margin-bottom: 60px;

  @media print {
    display: flex !important;
    justify-content: flex-end !important;
  }
`

export const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

export const Logo = styled.img`
  max-width: 160px;
  height: auto;
  object-fit: contain;

  @media print {
    max-width: 160px !important;
    height: auto !important;
    min-height: auto !important;
    ${printStyles}
  }
`

export const Tagline = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-style: italic;
  color: ${cardisTheme.colors.primary};
  margin: 20px 20px 0px 0px;
  text-align: right;

  @media print {
    font-size: 12px !important;
    margin: 20px 20px 0px 0px !important;
    ${printStyles}
  }
`

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 40px 64px;
  min-height: 0;

  @media print {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const TitleSection = styled.div`
  margin-bottom: 164px;
`

export const Title = styled.h1`
  font-family: ${cardisTheme.fonts.body};
  font-size: 45px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.1;

  @media print {
    font-size: 45px !important;
    font-weight: 600 !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.1 !important;
    ${printStyles}
  }
`

export const ReportDate = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 6px 0 0 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media print {
    font-size: 14px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 6px 0 0 0 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    ${printStyles}
  }
`

export const PropertySection = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media print {
    flex: 1 !important;
    display: flex !important;
  }
`

export const PropertyInfo = styled.div`
  flex: 1;
  max-width: 50%;
`

export const PropertyType = styled.h2`
  font-family: ${cardisTheme.fonts.body};
  font-size: 32px;
  font-weight: 500;
  font-style: italic;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 8px 0;

  @media print {
    font-size: 32px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 8px 0 !important;
    font-style: italic !important;
    font-weight: 500 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    ${printStyles}
  }
`

export const PropertyAddress = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 16px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.5;

  @media print {
    font-size: 16px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.5 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 400 !important;
    ${printStyles}
  }
`

export const ClientSection = styled.div`
  margin-top: 80px;
`

export const SectionLabel = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media print {
    font-size: 15px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 8px 0 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    font-family: ${cardisTheme.fonts.body} !important;
    ${printStyles}
  }
`

export const ClientName = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};

  @media print {
    font-size: 16px !important;
    color: ${cardisTheme.colors.primary} !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 600 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const ClientContact = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.6;

  @media print {
    font-size: 14px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.6 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 400 !important;
    ${printStyles}
  }
`

export const BrokerSection = styled.div`
  display: flex;
  flex-direction: column;

  @media print {
    display: flex !important;
    flex-direction: column !important;
  }
`

export const BrokerPhoto = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid ${cardisTheme.colors.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 16px;

  @media print {
    width: 200px !important;
    height: 200px !important;
    overflow: hidden !important;
    min-height: auto !important;
    border-radius: 50% !important;
    margin-bottom: 16px !important;
    ${printStyles}
  }
`

export const BrokerPhotoPlaceholder = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: ${cardisTheme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid ${cardisTheme.colors.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 16px;
  color: ${cardisTheme.colors.white};
  font-size: 48px;
  font-weight: 600;

  @media print {
    width: 140px !important;
    height: 140px !important;
    border-radius: 50% !important;
    background: ${cardisTheme.colors.primary} !important;
    ${printStyles}
  }
`

export const BrokerLabel = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-style: italic;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 8px 0;

  @media print {
    font-size: 14px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 8px 0 !important;
    font-style: italic !important;
    font-family: ${cardisTheme.fonts.body} !important;
    ${printStyles}
  }
`

export const BrokerName = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 22px;
  font-weight: 700;
  color: ${cardisTheme.colors.primary};

  @media print {
    font-size: 22px !important;
    color: ${cardisTheme.colors.primary} !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 700 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const BrokerContact = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.6;

  @media print {
    font-size: 14px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.6 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 400 !important;
    ${printStyles}
  }
`

export const BrokerPhone = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 17px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  letter-spacing: 1px;

  @media print {
    font-size: 17px !important;
    color: ${cardisTheme.colors.primary} !important;
    letter-spacing: 1px !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 600 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const FooterWrapper = styled.div`
  position: relative;
  margin-top: auto;

  @media print {
    position: relative !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
`

export const FooterPattern = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 200px;
  z-index: 0;
  object-fit: cover;
  background-color: white;

  @media print {
    position: absolute !important;
    bottom: 0 !important;
    left: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: 200px !important;
    object-fit: cover !important;
    overflow: hidden !important;
    border: none !important;
    ${printStyles}
  }
`

export const Footer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 60px 64px;

  @media print {
    position: relative !important;
    z-index: 1 !important;
    margin: 0 !important;
    padding: 60px 64px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-end !important;
    border: none !important;
  }
`

export const OfficeInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

export const LocationPinIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;

  @media print {
    width: 24px !important;
    height: 24px !important;
    min-height: auto !important;
    object-fit: contain !important;
    ${printStyles}
  }
`

export const OfficeDetails = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`

export const OfficeAddress = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 15px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.5;
  white-space: pre-line;
`

export const OfficePhone = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 4px 0 0 0;
`

export const CompanyContact = styled.div`
  text-align: right;
`

export const CompanyEmail = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
`

export const CompanyWebsite = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 4px 0 0 0;
`
